import React from 'react'
import { gql, useMutation } from '@apollo/client'
import { DateTime } from 'luxon'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import BlockIcon from '@material-ui/icons/Block'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'

const useStyles = makeStyles(theme => ({
  detailsCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#FFEBEE',
    border: '1px solid #FFCDD2',
    borderRadius: 8,
  },
  detailsIcon: {
    color: '#B71C1C',
  },
  detailsTextCol: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  detailsDate: {
    fontWeight: 600,
    lineHeight: 1.3,
  },
  detailsTime: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}))

const isIsoString = val =>
  typeof val === 'string' && /\d{4}-\d{2}-\d{2}T/.test(val)

const formatTime12 = iso => {
  const dt = DateTime.fromISO(iso)
  return dt.minute === 0 ? dt.toFormat('h a') : dt.toFormat('h:mm a')
}

const formatBlockForDisplay = blockTime => {
  if (isIsoString(blockTime.startTime)) {
    const start = DateTime.fromISO(blockTime.startTime)
    const end = DateTime.fromISO(blockTime.endTime)
    const sameDay = start.hasSame(end, 'day')
    const date = sameDay
      ? start.toFormat('EEE, MMM d, yyyy')
      : `${start.toFormat('EEE, MMM d')} – ${end.toFormat('EEE, MMM d, yyyy')}`
    const time = blockTime.allDay
      ? 'All day'
      : `${formatTime12(blockTime.startTime)} – ${formatTime12(
        blockTime.endTime,
      )}`
    return { date, time }
  }

  const sameDay = blockTime.startDate === blockTime.endDate
  const date = sameDay
    ? blockTime.startDate
    : `${blockTime.startDate} – ${blockTime.endDate}`
  const time =
    blockTime.startTime === 'All day' || blockTime.endTime === 'All day'
      ? 'All day'
      : `${blockTime.startTime} – ${blockTime.endTime}`
  return { date, time }
}

const DESTROY_BLOCK_TIME = gql`
  mutation DestroyBlockTime($id: ID!) {
    destroyCoachAvailabilityBlockTime(id: $id) {
      success
      message
    }
  }
`

const BlockTimeDestroy = ({ blockTime, onCancel, onSuccess }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const [destroyBlockTime] = useMutation(DESTROY_BLOCK_TIME, {
    onCompleted: data => {
      if (data.destroyCoachAvailabilityBlockTime.success) {
        enqueueSnackbar('Blocked time deleted', { variant: 'success' })
        onSuccess && onSuccess()
      } else {
        enqueueSnackbar(
          data.destroyCoachAvailabilityBlockTime.message ||
            'Failed to delete blocked time',
          { variant: 'error' },
        )
      }
    },
  })

  const onSubmit = () => {
    destroyBlockTime({ variables: { id: blockTime.id } })
  }

  const { date, time } = formatBlockForDisplay(blockTime)
  const note = blockTime.blockNote

  return (
    <>
      <Box mb={2}>
        <Typography>
          Deleting this blocked time will allow students to schedule
          appointments with you during this time.
        </Typography>
      </Box>

      <Box className={classes.detailsCard} mb={2}>
        <BlockIcon className={classes.detailsIcon} fontSize="small" />
        <Box className={classes.detailsTextCol}>
          <Typography variant="body2" className={classes.detailsDate}>
            {date}
          </Typography>
          <Typography variant="body2" className={classes.detailsTime}>
            {[time, note].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      </Box>

      <Box mb={2}>
        <Typography>Are you sure you want to delete it?</Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button onClick={onCancel} size="large">
          Cancel
        </Button>
        <Box ml={2}>
          <Button
            variant="contained"
            size="large"
            className={classes.deleteButton}
            onClick={onSubmit}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default BlockTimeDestroy
